import React from "react";
import { Link } from "react-router-dom";
import Card from "../card";

/**
 * Cartão de indicador — o padrão visual que estava triplicado em `default/index.tsx`,
 * `capacity/components/CapacityDashboard.tsx` e `equipamentManagement/index.tsx`.
 *
 * A adição em relação às três cópias é `progress`: a barra "realizado vs meta". Ela aceita
 * `target` nulo de propósito — sem meta cadastrada a barra não aparece, em vez de desenhar 0%.
 */

export type KpiAccent = "blue" | "cyan" | "emerald" | "amber" | "red";

const ACCENTS: Record<KpiAccent, { tile: string; glow: string; bar: string }> = {
  blue: {
    tile: "bg-gradient-to-br from-dv-blue-500 to-dv-blue-600",
    glow: "rgba(20,102,227,0.20)",
    bar: "bg-gradient-to-r from-dv-blue-500 to-dv-cyan-400",
  },
  cyan: {
    tile: "bg-gradient-to-br from-dv-cyan-400 to-dv-cyan-600",
    glow: "rgba(28,160,241,0.22)",
    bar: "bg-gradient-to-r from-dv-cyan-400 to-dv-cyan-600",
  },
  emerald: {
    tile: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    glow: "rgba(16,185,129,0.20)",
    bar: "bg-gradient-to-r from-emerald-500 to-teal-400",
  },
  amber: {
    tile: "bg-gradient-to-br from-amber-500 to-orange-600",
    glow: "rgba(245,158,11,0.20)",
    bar: "bg-gradient-to-r from-amber-500 to-orange-500",
  },
  red: {
    tile: "bg-gradient-to-br from-red-500 to-red-600",
    glow: "rgba(239,68,68,0.20)",
    bar: "bg-gradient-to-r from-red-500 to-rose-500",
  },
};

export interface KpiProgress {
  value: number;
  /** `null`/`undefined` = sem meta cadastrada ⇒ a barra não é renderizada. */
  target?: number | null;
  label?: string;
}

export interface KpiCardProps {
  icon?: JSX.Element;
  label: string;
  value: string;
  hint?: string;
  accent?: KpiAccent;
  progress?: KpiProgress;
  /** Quando informado, o cartão inteiro vira link para o drill-down. */
  href?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  label,
  value,
  hint,
  accent = "blue",
  progress,
  href,
}) => {
  const a = ACCENTS[accent];

  const hasTarget = progress != null && progress.target != null && progress.target > 0;
  const pct = hasTarget
    ? Math.min(100, Math.round((progress!.value / progress!.target!) * 100))
    : 0;

  const body = (
    <Card extra="group relative h-full overflow-hidden p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-dv-lg">
      <div
        className="pointer-events-none absolute -right-10 -top-12 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 group-hover:opacity-90"
        style={{ background: a.glow }}
        aria-hidden="true"
      />
      {icon && (
        <div
          className={`relative mb-3 flex h-11 w-11 items-center justify-center rounded-2xl ${a.tile} text-white`}
          style={{ boxShadow: `0 12px 30px -10px ${a.glow}` }}
        >
          {icon}
        </div>
      )}
      <p className="relative text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <p className="relative font-display text-3xl font-bold tabular-nums tracking-tight text-navy-700 dark:text-white">
        {value}
      </p>

      {hasTarget && (
        <div className="relative mt-3">
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
            <div
              className={`h-full rounded-full ${a.bar} transition-all duration-500`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1 text-xs tabular-nums text-gray-700 dark:text-gray-300">
            {pct}% {progress!.label ?? "da meta"}
          </p>
        </div>
      )}

      {hint && <p className="relative mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </Card>
  );

  return href ? (
    <Link to={href} className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40">
      {body}
    </Link>
  ) : (
    body
  );
};

export default KpiCard;
